import React, { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import DragDropListsCore from '@components-clean/core/drag-drop-lists-core';
import SimpleDragDropItem from '@components-clean/core/drag-drop-lists-core/drag-drop-item-simple';
import { DragDropListItemProps } from '@components-clean/core/drag-drop-lists-core/drag-drop-lists-core.interface';
import EditCard from '@components-clean/core/edit-card';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { useBoolean, useTranslation } from '@hooks/index';
import { ShowError, ShowSuccess } from '@utils/notification';
import {
    defaultRiskDashboardConfig,
    defaultRiskDashboardConfigVariables,
    riskDashboardConfig,
    riskDashboardConfigVariables,
} from '@queries/generated/risk-assessment.generated';
import {
    RISK_DASHBOARD_ALL_PANELS,
    RISK_DASHBOARD_CONFIG,
    UPSERT_RISK_DASHBOARD_CONFIG,
} from '@queries/risk-assessment';

const COMPONENT_NAME = 'ramDashboardConfig';

export interface RAMDashboardConfigProps {
    orgId: string;
    canEdit: boolean;
}

const RAMDashboardConfig = ({ orgId, canEdit }: RAMDashboardConfigProps) => {
    const { t: tcommon } = useTranslation();
    const { t } = useTranslation('admin', { keyPrefix: 'manageRam.sections.dashboard' });
    const isEditMode = useBoolean(false);
    const { confirmDiscardChanges, setComponentHasChanges } = useUnsavedChangesContext();

    const onEdit = () => {
        isEditMode.setTrue();
    };

    const { data: savedConfig } = useQuery<riskDashboardConfig, riskDashboardConfigVariables>(RISK_DASHBOARD_CONFIG);

    const { data: allPanelsData } = useQuery<defaultRiskDashboardConfig, defaultRiskDashboardConfigVariables>(
        RISK_DASHBOARD_ALL_PANELS,
    );

    const [saveRAMDashboardConfig] = useMutation(UPSERT_RISK_DASHBOARD_CONFIG);

    const [list1data, setList1data] = useState<DragDropListItemProps[]>([]);
    const [list2data, setList2data] = useState<DragDropListItemProps[]>([]);

    const [list1DataInitialState, setList1DataInitialState] = useState<DragDropListItemProps[]>([]);
    const [list2DataInitialState, setList2DataInitialState] = useState<DragDropListItemProps[]>([]);

    const [selectedItems, setSelectedItems] = useState<DragDropListItemProps[] | null>(null);

    useEffect(() => {
        const panels = savedConfig?.riskDashboardConfig?.panels ?? [];
        const mappedData = panels.map((item) => ({
            title: t(item?.panelId ?? ''),
            panelId: item?.panelId,
            active: true,
        }));

        setList1data(mappedData);
        setList1DataInitialState(mappedData);
    }, [savedConfig]);

    useEffect(() => {
        const panels = allPanelsData?.defaultRiskDashboardConfig?.panels ?? [];
        const existingIds = new Set(list1data.map((item) => item.panelId));

        const mappedPanels = panels
            .filter((panel) => panel?.panelId && !existingIds.has(panel.panelId))
            .sort((a, b) => (a?.displayOrder ?? 0) - (b?.displayOrder ?? 0))
            .map((item) => ({
                title: t(item?.panelId ?? ''),
                panelId: item?.panelId,
                active: true,
            }));

        setList2data(mappedPanels);
        setList2DataInitialState(mappedPanels);
    }, [allPanelsData, list1data]);

    const saveItems = () => {
        const updatedPanels = selectedItems?.map((item, index) => ({
            panelId: item.panelId,
            displayOrder: index + 1,
        }));
        saveRAMDashboardConfig({
            variables: {
                RiskDashboardConfig: {
                    organisationId: orgId,
                    panels: updatedPanels,
                },
            },
        })
            .then(() => {
                isEditMode.setFalse();
                setSelectedItems(null);
                ShowSuccess(tcommon('actionsSuccessfulToast'));
            })
            .catch(() => {
                isEditMode.setFalse();
                ShowError(t('errorMessage'));
            });
    };

    const hasChanges =
        selectedItems != null &&
        JSON.stringify(selectedItems.map((w) => w.panelId)) !==
            JSON.stringify(list1DataInitialState.map((w) => w.panelId));

    const onCancel = () => {
        setList1data([...list1DataInitialState]);
        setList2data([...list2DataInitialState]);
        setSelectedItems(null);
        isEditMode.setFalse();
    };

    const onEditCardCancel = async () => {
        if (hasChanges) {
            const ok = await confirmDiscardChanges('confirmation-dialog');
            if (!ok) {
                return;
            }
            onCancel();
        }
        onCancel();
    };

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, hasChanges);
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
    }, [hasChanges]);

    return (
        <EditCard
            title={t('title')}
            onEdit={onEdit}
            onCancel={onEditCardCancel}
            onSave={saveItems}
            isEditMode={isEditMode.value}
            data-testid="dashboard-config"
            saveDisabled={!hasChanges}
            className="k-mb-4"
            disabled={!canEdit}
        >
            <div style={{ height: '230px' }}>
                <DragDropListsCore
                    list1Header={t('leftColumnTitle')}
                    list2Header={t('rightColumnTitle')}
                    list1Items={list1data}
                    list2Items={list2data}
                    isTwoLists
                    idField="panelId"
                    onChangeItemsLeft={(items: DragDropListItemProps[]) => {
                        setSelectedItems([...items]);
                    }}
                    disabled={!isEditMode.value}
                    className="k-mb-4"
                    disableRightColSort
                    renderItemLeft={(item) => {
                        return <SimpleDragDropItem {...item} />;
                    }}
                    renderItemRight={(item) => {
                        return <SimpleDragDropItem {...item} />;
                    }}
                />
            </div>
        </EditCard>
    );
};

export default RAMDashboardConfig;
